import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthLoadingView } from 'shared-ui/lib/components/auth/AuthLoadingView';
import { Image } from 'shared-ui/lib/components/Image';
import { setupQueryConfig } from 'shared-ui/lib/config/query.config';
import { formatDate } from 'shared-ui/lib/utils/format-date';
import { useParams } from 'shared-ui/lib/utils/use-params';
import { ServiceEventAttachmentsModal } from '../../components/scenes/service-event/ServiceEventAttachmentsModal';
import { routesConfig } from '../../config/routes.config';
import UserContext from '../../contexes/UserContext';
import { useServiceEventQuery } from '../../generated/graphql';

type ServiceEventShowLocationState = {
  openServiceEventAttachments?: boolean;
};

export const ServiceEventShowScene = () => {
  const { t } = useTranslation('ServiceEventShowScene');
  const { t: tVehicle } = useTranslation('VehicleShowScene');
  const location = useLocation();
  const navigate = useNavigate();
  const { vehicleId, id } = useParams<{ vehicleId: string; id: string }>();
  const { data, loading, refetch } = useServiceEventQuery(setupQueryConfig({ id }));
  const [isAttachmentsOpen, setIsAttachmentsOpen] = useState(false);
  const [whoAmIUser] = useContext(UserContext);

  useEffect(() => {
    const state = location.state as ServiceEventShowLocationState | null;
    if (state?.openServiceEventAttachments) {
      setIsAttachmentsOpen(true);
      navigate(`${location.pathname}${location.search}`, { replace: true, state: {} });
    }
  }, [location, navigate]);

  if (loading || !data) return <AuthLoadingView />;

  const { serviceEvent } = data;
  const myPartnerId = whoAmIUser.partner?.id ?? null;
  const canAddServiceEventAttachments =
    myPartnerId != null &&
    serviceEvent.partnerId != null &&
    serviceEvent.partnerId === myPartnerId;

  return (
    <div className="mx-auto w-full max-w-3xl sm:px-2">
      <div className="bg-white py-8 px-6 sm:px-10 shadow rounded-lg">
        <h2 className="text-lg leading-6 font-bold text-gray-900">{serviceEvent.name}</h2>
        <div className="mt-6">
          <div className="max-w-screen-lg mt-6 mx-auto mb-4">
            <Image src={serviceEvent.photo} className="mx-auto rounded-md" w={672} />
          </div>
          <div className="max-w-screen-lg mx-auto mb-4 flex items-center space-x-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-primary flex-shrink-0"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
            </svg>
            <button
              type="button"
              onClick={() => setIsAttachmentsOpen(true)}
              className="text-primary hover:text-secondary text-sm font-medium underline"
            >
              {tVehicle('attachmentsLink', { count: serviceEvent.attachments.length })}
            </button>
          </div>
          <ServiceEventAttachmentsModal
            isOpen={isAttachmentsOpen}
            onClose={() => setIsAttachmentsOpen(false)}
            serviceEventId={id}
            attachments={serviceEvent.attachments}
            hasActiveLicense={serviceEvent.vehicle.hasActiveLicense}
            canAddAttachments={canAddServiceEventAttachments}
            attachmentsActionsLocked={serviceEvent.isTimeLocked}
            onRefetch={() => {
              refetch();
            }}
          />
          {serviceEvent.partner?.name ? (
            <div className="grid grid-cols-2 pb-3 space-x-2">
              <p className="text-lg text-gray-900">{t('partner')}</p>
              <p className="text-lg text-gray-color">{serviceEvent.partner.name}</p>
            </div>
          ) : null}
          <div className="grid grid-cols-2 pb-3 space-x-2">
            <p className="text-lg text-gray-900">{t('serviceEventType')}</p>
            <p className="text-lg text-gray-color">{t(`ServiceEventType:${serviceEvent.type}`)}</p>
          </div>
          <div className="grid grid-cols-2 pb-3 space-x-2">
            <p className="text-lg text-gray-900">{t('serviceEventServiceDate')}</p>
            <p className="text-lg text-gray-color">{formatDate(serviceEvent.serviceDate)}</p>
          </div>
          <div className="grid grid-cols-2 pb-3 space-x-2">
            <p className="text-lg text-gray-900">{t('serviceEventMileage')}</p>
            <p className="text-lg text-gray-color">{serviceEvent.mileage}</p>
          </div>
          <div className="grid grid-cols-2 pb-3 space-x-2">
            <p className="text-lg text-gray-900">{t('serviceEventRunningHours')}</p>
            <p className="text-lg text-gray-color">{serviceEvent.runningHours}</p>
          </div>
          <div className="grid grid-cols-1 pb-3">
            <p className="text-lg text-gray-900">{t('serviceEventDescription')}</p>
            <p className="text-md mt-4 text-gray-color">{serviceEvent.description}</p>
          </div>
        </div>
        <Link
          className="inline-flex justify-center py-2 px-4 border transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary disabled:bg-gray-300"
          to={routesConfig.VEHICLES.SHOW.replace(':id', vehicleId)}
        >
          {t('backLink')}
        </Link>
      </div>
    </div>
  );
};
